/**
 * Shows the cart total next to the cart icon and keeps it in sync after
 * cart changes (add to cart, quantity updates, removals).
 */
class CartTotal extends HTMLElement {
  connectedCallback() {
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#onCartUpdate);
  }

  #onCartUpdate = async (event) => {
    if (event.detail?.data?.didError) return;

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      this.textContent = formatMoney(cart.total_price, this.dataset.moneyFormat);
    } catch {
      // Keep the last rendered total when the request fails
    }
  };
}

/**
 * Formats cents using the shop's money format, e.g. "€{{amount_with_comma_separator}}".
 * @param {number} cents
 * @param {string} [format]
 * @returns {string}
 */
function formatMoney(cents, format = '€{{amount_with_comma_separator}}') {
  const withDelimiters = (value, precision, thousands, decimal) => {
    const [whole, fraction] = (value / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(cents, 0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(cents, 0, '.', ',');
      case 'amount_with_space_separator':
        return withDelimiters(cents, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return withDelimiters(cents, 0, ' ', ',');
      case 'amount_with_period_and_space_separator':
        return withDelimiters(cents, 2, ' ', '.');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(cents, 2, "'", '.');
      default:
        return withDelimiters(cents, 2, ',', '.');
    }
  });
}

if (!customElements.get('cart-total')) {
  customElements.define('cart-total', CartTotal);
}
